import { memo, useCallback, useState } from "react";
import { Bookmark, BookmarkCheck, TrendingUp } from "lucide-react";
import styles from "./PostCard.module.scss";
import { isKnownFallbackImageUrl, buildTitleImageUrl } from "../../utils/insightImage";
import { getBookmarkedIds, toggleBookmark } from "../../utils/bookmarks";

export interface PostCardPost {
  id: number;
  slug: string;
  title: string;
  description?: string;
  imageUrl?: string;
  category?: string;
  trending?: boolean;
  readingTime?: number;
  views?: number;
}

interface PostCardProps {
  post: PostCardPost;
  onClick?: () => void;
  /**
   * If provided, uses a fallback video when the image is missing.
   * Defaults to the TICCA blog general video.
   */
  fallbackVideoUrl?: string;
  /**
   * Show bookmark toggle (default true on the insights grid).
   */
  showBookmark?: boolean;
  /**
   * Called after the bookmark state toggles (so parents can refresh filters).
   */
  onBookmarkChange?: () => void;
  /**
   * Show category chip (default true).
   */
  showCategory?: boolean;
  /**
   * Show description preview (default true).
   */
  showDescription?: boolean;
  /**
   * Framer-motion style entry animation (default true).
   */
  animate?: boolean;
  index?: number;
}

/**
 * Shared blog post tile — identical markup + styling used by the
 * Insights grid and the "You may also want to read" section.
 */
function PostCard({
  post,
  onClick,
  fallbackVideoUrl = "https://public.ticca.com/video/BlogGeneralvideo.mp4",
  showBookmark = true,
  onBookmarkChange,
  showCategory = true,
  showDescription = true,
  animate = true,
  index = 0,
}: PostCardProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(() =>
    getBookmarkedIds().has(post.id),
  );

  const handleBookmark = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const added = toggleBookmark({ id: post.id, title: post.title, slug: post.slug });
      setIsBookmarked(added);
      onBookmarkChange?.();
    },
    [post, onBookmarkChange],
  );

  const imageSrc = post.imageUrl || buildTitleImageUrl(post.id, post.title) || "";
  const showFallbackVideo = !imageSrc || imageFailed;

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* IMAGE */}
      <div className={styles.imageWrap}>
        {showFallbackVideo ? (
          <video
            src={fallbackVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            className={styles.image}
            controls={false}
          />
        ) : (
          <img
            src={imageSrc}
            alt={post.title}
            className={styles.image}
            loading="lazy"
            onLoad={(event) => {
              const el = event.currentTarget;
              const loadedUrl = (el.currentSrc || el.src || "").toLowerCase();
              if (isKnownFallbackImageUrl(loadedUrl)) {
                setImageFailed(true);
              }
            }}
            onError={() => setImageFailed(true)}
          />
        )}

        {post.trending && (
          <div className={styles.trendingBadge}>
            <TrendingUp className={styles.trendingIcon} />
            Trending
          </div>
        )}

        {showBookmark && (
          <button
            onClick={handleBookmark}
            className={styles.bookmarkBtn}
            title={isBookmarked ? "Remove bookmark" : "Bookmark this post"}
          >
            {isBookmarked ? (
              <BookmarkCheck className={styles.bookmarkIconFilled} />
            ) : (
              <Bookmark className={styles.bookmarkIcon} />
            )}
          </button>
        )}
      </div>

      {/* CONTENT */}
      <div className={styles.body}>
        {showCategory && post.category && (
          <span className={styles.categoryChip}>{post.category}</span>
        )}

        <h3 className={styles.title}>{post.title}</h3>

        {showDescription && post.description && (
          <p className={styles.description}>{post.description}</p>
        )}
      </div>
    </div>
  );
}

export default memo(PostCard);
